export function addMinutosHora(timeStr, mins) {
  const [h, m] = String(timeStr || "10:00").split(":").map(Number);
  const total = h * 60 + m + Number(mins || 45);
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function buildCitaDateTimeISO(fecha, hora) {
  if (!fecha || !hora) return null;
  const d = new Date(`${fecha}T${hora}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function parsePgUuidArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean).map(String);
  if (typeof val === "string" && val.startsWith("{")) {
    const inner = val.slice(1, -1);
    if (!inner.trim()) return [];
    return inner.split(",").map((s) => s.replace(/^"|"$/g, "").trim()).filter(Boolean);
  }
  return [];
}

export function isoToFechaHoraLocal(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { fecha: "", hora: "" };
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { fecha: `${yyyy}-${mo}-${dd}`, hora: `${hh}:${mm}` };
}

export function snapDuracionVisita(min) {
  const opts = [30, 45, 60, 90];
  const n = Number(min) || 45;
  return opts.reduce((best, o) => (Math.abs(o - n) < Math.abs(best - n) ? o : best), 45);
}

export function mapPropiedadParaModalCita(det, propietarios = []) {
  let propsNorm = propietarios;
  if (typeof propsNorm === "string") {
    try { propsNorm = JSON.parse(propsNorm); } catch { propsNorm = []; }
  }
  if (!Array.isArray(propsNorm)) propsNorm = [];
  return {
    id: det.id,
    titulo: det.titulo || "Sin título",
    detalle: `${det.tipo_nombre || det.tipo || ""} · ${det.poblacion_nombre || det.poblacion || ""}`,
    precio: det.precio ? `${Number(det.precio).toLocaleString("es-ES")} €` : "Consultar",
    referencia: det.referencia,
    tenemos_llaves: !!(
      det.tenemos_llaves === true
      || det.tenemos_llaves === 1
      || det.tenemos_llaves === '1'
      || det.tenemos_llaves === 't'
      || det.tenemos_llaves === 'true'
      || det.tenemos_llaves === 'TRUE'
    ),
    num_llave: det.num_llave || null,
    recoger_llaves_en: det.recoger_llaves_en || null,
    propietarios: propsNorm,
    info_visita: det.info_visita != null ? String(det.info_visita) : "",
  };
}

/** Propiedad anotada a mano (sin ficha CRM). */
export function isPropiedadTextoLibre(p) {
  if (!p) return false;
  if (p.esTextoLibre) return true;
  const id = String(p.id || "");
  return id.startsWith("txt:");
}

export function mapPropiedadTextoLibre(texto) {
  const t = String(texto || "").trim();
  if (!t) return null;
  return {
    id: `txt:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    titulo: t,
    referencia: t,
    detalle: "Texto libre",
    precio: "",
    esTextoLibre: true,
    propietarios: [],
    info_visita: "",
  };
}

export function parseMetadataMovil(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return { ...raw };
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw);
      return o && typeof o === "object" ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** Cliente / propiedades libres guardados en metadata_movil (+ fallback título). */
export function readAgendaTextoLibre(cita) {
  const meta = parseMetadataMovil(cita?.metadata_movil);
  let clienteTexto = meta.cliente_texto != null ? String(meta.cliente_texto).trim() : "";
  if (!clienteTexto && cita?.titulo) {
    const fromTitulo = String(cita.titulo).replace(/^Visita\s*[—\-–]\s*/i, "").trim();
    if (fromTitulo && !/^visita$/i.test(fromTitulo) && !cita.cliente_id && !cita.contacto_id && !cita.lead_id) {
      clienteTexto = fromTitulo;
    }
  }
  const propsTexto = Array.isArray(meta.propiedades_texto)
    ? meta.propiedades_texto.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  return { clienteTexto, propsTexto, meta };
}

export function splitPropiedadesCita(props = []) {
  const reales = [];
  const texto = [];
  for (const p of props || []) {
    if (!p) continue;
    if (isPropiedadTextoLibre(p)) {
      const label = String(p.titulo || p.referencia || "").trim();
      if (label) texto.push(label);
    } else if (p.id) {
      reales.push(p);
    }
  }
  return { reales, texto };
}

async function validarRefUnaVez(api, cand) {
  const r = await api.get(`/propiedades/validar-ref/${encodeURIComponent(cand)}`);
  return r.data?.propiedad || r.data?.data || null;
}

/** Busca ficha por ref CRM (ModalCita). Reintento + fallbacks: 1ª vez a veces falla en frío. */
export async function fetchPropiedadPorRef(api, ref) {
  const trimmed = String(ref || "")
    .trim()
    .replace(/[\u2010-\u2015\u2212]/g, "-") // guiones tipográficos → ASCII
    .replace(/\s+/g, "");
  if (!trimmed) return null;
  // Ampliar: tal cual, mayúsculas, y con guión normalizado (3598-C / 3598C).
  const candidates = [];
  const pushCand = (v) => {
    const s = String(v || "").trim();
    if (s && !candidates.includes(s)) candidates.push(s);
  };
  pushCand(trimmed);
  pushCand(trimmed.toUpperCase());
  if (/^\d{2,6}[A-Za-z]$/i.test(trimmed)) {
    pushCand(`${trimmed.slice(0, -1)}-${trimmed.slice(-1).toUpperCase()}`);
  }
  let p = null;
  for (const cand of candidates) {
    for (let attempt = 0; attempt < 2 && !p?.id; attempt++) {
      try {
        p = await validarRefUnaVez(api, cand);
        if (p?.id) break;
      } catch {
        if (attempt === 0) await new Promise((r) => setTimeout(r, 180));
      }
    }
    if (p?.id) break;
  }
  // Fallback: listado / ubicacion-busqueda (misma ref visible en cruce de demanda).
  if (!p?.id) {
    try {
      const r = await api.get(
        `/propiedades?buscar=${encodeURIComponent(trimmed)}&cartera=todas&perPage=10`
      );
      const lista = r.data?.data || [];
      const norm = trimmed.toUpperCase();
      const exacta = lista.find(
        (row) => String(row.referencia || "").trim().toUpperCase().replace(/\s+/g, "") === norm
      );
      if (exacta?.id) p = exacta;
    } catch { /* siguiente */ }
  }
  if (!p?.id) {
    try {
      const r = await api.get(`/propiedades/ubicacion-busqueda?ref=${encodeURIComponent(trimmed)}`);
      if (r.data?.encontrada && r.data.id) p = { id: r.data.id, referencia: trimmed };
    } catch { /* fin */ }
  }
  if (!p?.id) return null;
  let det = p;
  try {
    const detRes = await api.get(`/propiedades/${p.id}`);
    det = detRes.data?.data || detRes.data?.propiedad || detRes.data || p;
  } catch { /* usar p */ }
  let propietarios = [];
  try {
    const propRes = await api.get(`/propiedades/${p.id}/propietarios`);
    propietarios = propRes.data?.data || propRes.data || [];
  } catch {
    if (Array.isArray(det.propietarios)) propietarios = det.propietarios;
    else if (typeof det.propietarios === "string") {
      try { propietarios = JSON.parse(det.propietarios); } catch { propietarios = []; }
    }
  }
  return mapPropiedadParaModalCita(det, propietarios);
}

export async function fetchPropiedadParaModalCita(api, propId) {
  const detRes = await api.get(`/propiedades/${propId}`);
  const det = detRes.data?.data || detRes.data?.propiedad || detRes.data;
  if (!det?.id) return null;
  let propietarios = [];
  try {
    const propRes = await api.get(`/propiedades/${propId}/propietarios`);
    propietarios = propRes.data?.data || propRes.data || [];
  } catch {
    if (Array.isArray(det.propietarios)) propietarios = det.propietarios;
    else if (typeof det.propietarios === "string") {
      try { propietarios = JSON.parse(det.propietarios); } catch { propietarios = []; }
    }
  }
  return mapPropiedadParaModalCita(det, propietarios);
}

/** Refs de leads activos del cliente (p. ej. nuevo lead por Ref. 3515). */
export async function fetchLeadPropiedadRefs(api, { clienteId, leadId } = {}) {
  const refs = new Set();
  const addRef = (ref) => {
    const t = String(ref || "").trim();
    if (t) refs.add(t);
  };

  if (leadId) {
    try {
      const lr = await api.get(`/leads/${leadId}`);
      const lead = lr.data?.data || lr.data;
      addRef(lead?.propiedad_ref);
    } catch { /* ignore */ }
  }

  if (clienteId) {
    try {
      const lr = await api.get(`/leads?cliente_id=${clienteId}`);
      const lista = lr.data?.data || lr.data || [];
      for (const lead of lista) {
        if (lead?.activo === false) continue;
        addRef(lead?.propiedad_ref);
      }
    } catch { /* ignore */ }
  }

  return [...refs];
}

/**
 * Carga cita de agenda para ModalCita (edición).
 * Por defecto la fuente de verdad es el propiedad_ids de la cita. Si se pide
 * mergeLeadRefs=true, además añade las refs de leads activos del cliente que no
 * estén ya en la cita (sólo para flujos de sugerencia; en edición normal NO se
 * usa para no reinyectar propiedades que el agente ha quitado a mano).
 * Permite citas sin propiedades CRM (hueco / texto libre / citas antiguas).
 */
export async function loadCitaModalFromAgenda(api, agendaId, { mergeLeadRefs = false } = {}) {
  const res = await api.get(`/agenda/${agendaId}`);
  const cita = res.data?.data || res.data;
  if (!cita?.id) throw new Error("No se encontró la cita en agenda.");

  const propIds = parsePgUuidArray(cita.propiedad_ids);
  const propiedadesModal = (
    await Promise.all(propIds.map((pid) => fetchPropiedadParaModalCita(api, pid)))
  ).filter(Boolean);

  if (mergeLeadRefs) {
    const leadRefs = await fetchLeadPropiedadRefs(api, {
      clienteId: cita.cliente_id,
      leadId: cita.lead_id,
    });
    for (const ref of leadRefs) {
      const ya = propiedadesModal.some(
        (p) => String(p.referencia || "").toUpperCase() === ref.toUpperCase(),
      );
      if (ya) continue;
      const prop = await fetchPropiedadPorRef(api, ref);
      if (prop?.id) propiedadesModal.push(prop);
    }
  }

  const { clienteTexto, propsTexto } = readAgendaTextoLibre(cita);
  for (const t of propsTexto) {
    const ya = propiedadesModal.some(
      (p) => isPropiedadTextoLibre(p) && String(p.titulo || "").trim().toLowerCase() === t.toLowerCase(),
    );
    if (ya) continue;
    const stub = mapPropiedadTextoLibre(t);
    if (stub) propiedadesModal.push(stub);
  }

  const { fecha, hora: horaInicio } = isoToFechaHoraLocal(cita.fecha_inicio);
  const { hora: horaFinAgenda } = isoToFechaHoraLocal(cita.fecha_fin);
  const fin = new Date(cita.fecha_fin);
  const inicio = new Date(cita.fecha_inicio);
  const nSlots = Math.max(1, propiedadesModal.length);
  const totalMin = Number.isNaN(fin.getTime()) || Number.isNaN(inicio.getTime())
    ? nSlots * 45
    : Math.max(15, Math.round((fin.getTime() - inicio.getTime()) / 60000));
  // Duración EXACTA por visita (sin redondear a 30/45/60/90): así la hora fin que
  // se muestra coincide siempre con la fecha_fin guardada y, si algo recalcula la
  // hora fin (inicio + duración), reproduce el valor exacto en vez de desviarse.
  const duracion = Math.max(5, Math.round(totalMin / nSlots));

  return {
    cita,
    propiedadesModal,
    clienteTexto,
    initialValues: { fecha, horaInicio, horaFin: horaFinAgenda || undefined, duracion },
  };
}

function parseTaskMetadataRaw(tarea) {
  if (!tarea?.metadata) return {};
  if (typeof tarea.metadata === "object") return tarea.metadata;
  try {
    return JSON.parse(tarea.metadata || "{}");
  } catch {
    return {};
  }
}

/** agenda_id en metadata de tarea (T4/TC o copiado en T4b*). */
export function getAgendaIdFromTaskMetadata(meta = {}) {
  const id = meta.agenda_id || meta.visit_agenda_id || meta.source_agenda_id;
  return id ? String(id) : null;
}

/**
 * T4b1/T4b2 no siempre llevan agenda_id: resolver desde la tarea TC/T4 del lead
 * o desde cualquier tarea del historial que tenga cita vinculada.
 */
export async function resolveVisitAgendaIdForLead(api, tarea) {
  const meta = parseTaskMetadataRaw(tarea);
  const direct = getAgendaIdFromTaskMetadata(meta);
  if (direct) return direct;

  const leadId = tarea?.lead_id;
  if (!leadId) return null;

  try {
    const res = await api.get(`/tareas/lead/${leadId}/todas`);
    const tareas = res.data?.tareas || [];
    const visitCodes = new Set(["TC", "T4"]);
    for (const t of tareas) {
      const m = parseTaskMetadataRaw(t);
      const code = String(m.task_code || "").toUpperCase();
      if (!visitCodes.has(code)) continue;
      const aid = getAgendaIdFromTaskMetadata(m);
      if (aid) return aid;
    }
    for (const t of tareas) {
      const aid = getAgendaIdFromTaskMetadata(parseTaskMetadataRaw(t));
      if (aid) return aid;
    }
  } catch {
    /* ignore */
  }

  return null;
}

/**
 * Contexto para ModalCita al programar segunda visita desde M2.
 * Si viene `propiedadIds` (las cualificadas como «Segunda visita»), solo esas
 * entran en el modal — no las descartadas de la cita original.
 */
export async function loadSegundaVisitaModalContext(
  api,
  tarea,
  { clienteId = null, propiedadIds = null } = {},
) {
  const wantIds = [
    ...new Set(
      (Array.isArray(propiedadIds) ? propiedadIds : [])
        .map((id) => String(id || "").trim())
        .filter((id) => id && !id.startsWith("ref-")),
    ),
  ];

  const filtrarSoloSegunda = (lista) => {
    if (!wantIds.length) return lista || [];
    const byId = new Map((lista || []).map((p) => [String(p.id), p]));
    return wantIds.map((id) => byId.get(id)).filter(Boolean);
  };

  const agendaId = await resolveVisitAgendaIdForLead(api, tarea);
  if (agendaId) {
    const loaded = await loadCitaModalFromAgenda(api, agendaId, { mergeLeadRefs: false });
    let propiedadesModal = filtrarSoloSegunda(loaded.propiedadesModal);
    // Si la cita original no tenía el id (o falló el fetch), completar por id.
    if (wantIds.length && propiedadesModal.length < wantIds.length) {
      const have = new Set(propiedadesModal.map((p) => String(p.id)));
      for (const pid of wantIds) {
        if (have.has(pid)) continue;
        const extra = await fetchPropiedadParaModalCita(api, pid);
        if (extra?.id) {
          propiedadesModal.push(extra);
          have.add(String(extra.id));
        }
      }
    }
    return { ...loaded, propiedadesModal, agendaId };
  }

  let propiedadesModal = [];
  if (wantIds.length) {
    propiedadesModal = (
      await Promise.all(wantIds.map((pid) => fetchPropiedadParaModalCita(api, pid)))
    ).filter(Boolean);
  } else {
    const refs = await fetchLeadPropiedadRefs(api, {
      clienteId: clienteId || tarea?.cliente_id,
      leadId: tarea?.lead_id,
    });
    propiedadesModal = (
      await Promise.all(refs.map((ref) => fetchPropiedadPorRef(api, ref)))
    ).filter(Boolean);
  }

  return {
    cita: {
      agente_id: tarea?.agente_id || null,
      cliente_id: clienteId || tarea?.cliente_id || null,
      lead_id: tarea?.lead_id || null,
      titulo: "Visita",
    },
    propiedadesModal,
    clienteTexto: "",
    initialValues: null,
    agendaId: null,
  };
}
